<template>
  <div>
    <div class="flex items-center justify-between text-center text-neutral-800">
      <button
        type="button"
        :aria-label="$t('quote.select-previous-week')"
        class="rounded-full size-10 flex items-center justify-center focus:ring focus:ring-neutral-200 focus:outline-none hover:bg-neutral-100 transition duration-300"
        :class="{
          'cursor-pointer text-neutral-400': canSelectPreviousWeek,
          'cursor-not-allowed text-neutral-200': !canSelectPreviousWeek,
        }"
        @click="previousWeek"
      >
        <fa
          :icon="['fal', 'angle-left']"
          class="text-2xl fa-fw"
        />
      </button>
      <div class="font-normal text-sm text-neutral-700 select-none">
        {{ selectedDayLabel }}
      </div>
      <button
        type="button"
        :aria-label="$t('quote.select-next-week')"
        class="rounded-full size-10 flex items-center justify-center focus:ring focus:ring-neutral-200 focus:outline-none hover:bg-neutral-100 transition duration-300"
        :class="{
          'cursor-pointer text-neutral-400': canSelectNextWeek,
          'cursor-not-allowed text-neutral-200': !canSelectNextWeek,
        }"
        @click="nextWeek"
      >
        <fa
          :icon="['fal', 'angle-right']"
          class="text-2xl fa-fw"
        />
      </button>
    </div>

    <div class="mt-4 relative overflow-hidden h-16">
      <div
        v-for="(week, weekIndex) in getWeeks"
        :key="`week-${weekIndex}`"
      >
        <transition-group :name="slideDirection">
          <div
            v-if="selectedWeek === weekIndex"
            :key="`selected-week-${weekIndex}`"
            class="grid gap-1 grid-cols-7 w-full"
          >
            <div
              v-for="(day, dayIndex) in week"
              :key="`day-${weekIndex}-${dayIndex}`"
              class="flex flex-col items-center"
            >
              <div class="text-neutral-800 text-xs font-light text-center uppercase select-none">
                {{ day.dayLabel }}
              </div>
              <button
                type="button"
                :aria-label="$t('quote.select-day', { day: day.day })"
                class="flex items-center justify-center text-center text-lg rounded-full size-10 mt-1 transition duration-300 select-none focus:outline-none"
                :class="{
                  'bg-bubblegum-500 text-neutral-900': isSelectedDay(day),
                  'hover:bg-bubblegum-300 hover:text-white bg-transparent text-neutral-700':
                    !day.isInPast && !isSelectedDay(day) && !isDisabledDay(day),
                  'cursor-pointer': !day.isInPast,
                  'text-neutral-300': day.isInPast || (isDisabledDay(day) && !isSelectedDay(day)),
                }"
                @click="clickSelectDay(day)"
              >
                {{ day.day }}
              </button>
            </div>
          </div>
        </transition-group>
      </div>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon';

import { i18n } from '@/i18n';

export default {
  name: 'VetsterDayPicker',

  props: {
    date: {
      type: [Object, String],
      default: undefined,
    },
    // Used to build day picker when the date has been deselected
    oldDate: {
      type: Object,
      default: undefined,
    },
    timezone: {
      type: String,
      default: undefined,
    },
    isFilter: {
      type: Boolean,
      default: false,
    },
    disabledDates: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loaded: false,
      slideDirection: 'slide-next',
      selectedDay: undefined,
      selectedWeek: 0,
      weeks: [],
      today: undefined,
    };
  },

  computed: {
    selectedUnixTime() {
      return this.selectedDay?.object?.toMillis();
    },

    canSelectNextWeek() {
      return this.selectedWeek < 5;
    },

    canSelectPreviousWeek() {
      return this.selectedWeek > 0;
    },

    selectedDayLabel() {
      if (this.selectedDay) {
        return this.selectedDay.monthLabel;
      }

      if (this.oldDate) {
        return this.oldDate.monthLabel;
      }

      return this.$t('app.unknown');
    },

    getWeeks() {
      const weeks = [];
      let days = [];
      let day = {};

      for (let w = 0; w < 6; w += 1) {
        days = [];

        const dateCursor = DateTime.local()
          .setZone(this.timezone)
          .startOf('week');

        for (let d = 0; d < 7; d += 1) {
          day = dateCursor.plus({ week: w }).plus({ day: d });
          days.push(this.formatDay(day, this.getRelativeWeeks(w)));
        }

        weeks.push(days);
      }

      return weeks;
    },
  },

  watch: {
    selectedUnixTime(unixTime) {
      if (this.loaded) {
        this.$emit('update', unixTime ? this.selectedDay.object : null);
      }
    },

    date(newDate) {
      this.syncSelectedDay(newDate);
    },

    timezone(timezone, oldTimezone) {
      if (timezone === oldTimezone) {
        return;
      }

      // When selecting a timezone that will make "today" the next calendar day, make sure we adjust the selected day to be the next day.
      this.today = DateTime.local().setZone(timezone).startOf('day');
      let newDate = this.selectedDay?.object.setZone(timezone);

      if (!newDate || newDate < this.today) {
        newDate = this.today;
      }

      this.selectWeek(newDate);
    },
  },

  created() {
    this.syncSelectedDay(this.date);
  },

  mounted() {
    this.$nextTick(() => {
      this.loaded = true;
    });
  },

  methods: {
    selectWeek(day, week = null) {
      this.selectedWeek = week ?? this.determineSelectedWeek(day);
      this.selectedDay = this.formatDay(day, this.getRelativeWeeks(week));
    },
    syncSelectedDay(date) {
      this.today = DateTime.local().setZone(this.timezone).startOf('day');
      let selectedDay = this.today;

      // if date has been deselected
      if (!date) {
        selectedDay = this.oldDate;
      }

      if (date) {
        const newDate = DateTime.fromISO(date, {
          zone: this.timezone,
        }).startOf('day');

        if (selectedDay && !selectedDay.equals(newDate)) {
          this.selectWeek(newDate);

          return;
        }
      }

      if (date) {
        this.selectedDay = this.formatDay(
          selectedDay,
          this.getRelativeWeeks(this.selectedWeek),
        );
      }
    },

    determineSelectedWeek(newDate) {
      if (newDate.weekYear > this.today.weekYear) {
        return newDate.weekNumber + (
          this.today.weeksInWeekYear - this.today.weekNumber
        );
      }

      return newDate.weekNumber - this.today.weekNumber;
    },

    nextWeek() {
      const day = this.selectedDay || this.oldDate;
      this.slideDirection = 'slide-next';

      if (this.canSelectNextWeek) {
        this.selectWeek(day.object.plus({ week: 1 }), this.selectedWeek + 1);
        this.$emit('selected');
      }
    },

    previousWeek() {
      const day = this.selectedDay || this.oldDate;
      this.slideDirection = 'slide-previous';

      if (this.canSelectPreviousWeek) {
        let suggestedDay = day.object.minus({ week: 1 });

        if (suggestedDay < this.today) {
          suggestedDay = this.today;
        }

        this.selectWeek(suggestedDay, this.selectedWeek - 1);
        this.$emit('selected');
      }
    },

    async clickSelectDay(date) {
      if (this.isDisabledDay(date)) {
        return;
      }

      // Deselecting day
      if (this.isFilter && this.isSelectedDay(date)) {
        this.$store.commit('search/setDates', { old_date_from: this.selectedDay });
        this.selectedDay = null;
        this.$emit('selected');
        this.$emit('change', this.selectedDay);

        return;
      }

      if (this.selectDay(date)) {
        this.$emit('selected');
        this.$emit('change', this.selectedDay);
      }
    },

    selectDay(date) {
      if (date.object.startOf('day').equals(this.today)
        || date.object >= this.today
      ) {
        this.selectedDay = date;

        return true;
      }

      return false;
    },

    isSelectedDay(date) {
      return this.selectedDay
        && date.object
          .startOf('day')
          .equals(this.selectedDay.object.startOf('day'));
    },

    isInPast(date) {
      return date.object.startOf('day') < this.today;
    },

    getRelativeWeeks(w) {
      if (w > 0) {
        return w === 1
          ? `, ${this.$t('search.next-week')}`
          : `, ${this.$t('search.in-x-weeks', { weeks: w })}`;
      }

      return '';
    },

    reset() {
      this.slideDirection = 'slide-previous';
      this.today = DateTime.local().setZone(this.timezone).startOf('day');

      this.selectWeek(this.today, 0);
    },

    formatDay(date, relativeWeeks) {
      const day = date.setLocale(i18n.locale);

      return {
        object: day,
        dayLabel: day.toFormat('ccc'),
        monthLabel: `${day.toFormat('LLLL')} ${day.toFormat('y')}${relativeWeeks}`,
        day: day.toFormat('d'),
        month: day.toFormat('L'),
        year: day.toFormat('y'),
        ymd: day.toFormat('yyyy-LL-dd'),
        isInPast: day.startOf('day') < this.today,
      };
    },

    isDisabledDay(day) {
      return this.disabledDates.includes(day.ymd);
    },
  },
};
</script>
